/**
 * RBAC for RFQ Export Data on admin, customer & vendor portals.
 *
 * Viewing RFQs stays open to all authenticated portal users (rfqView / quotationView / adminRfqView).
 * Export is limited to Super Admin and Admin (Organization Administrator).
 */

export const RFQ_EXPORT_PERMISSION = 'rfqExport';

export type RfqExportPortal = 'admin' | 'customer' | 'vendor';

const CUSTOMER_EXPORT_ROLE_KEYS = new Set([
  'customer_admin', // Customer Super Admin
  'customer_org_admin', // Organization Administrator (Admin)
  'customer', // legacy Super Admin alias
]);

const VENDOR_EXPORT_ROLE_KEYS = new Set([
  'vendor_admin', // Vendor Super Admin
  'vendor_org_admin', // Organization Administrator (Admin)
  'vendor', // legacy Super Admin alias
]);

const ADMIN_EXPORT_ROLE_KEYS = new Set([
  'admin_super_admin',
  'admin_organization_administrator',
  'admin_platform_admin',
  'admin',
]);

/** Exact normalized display names / leftover labels that mean Super Admin or Admin. */
const EXPORT_DISPLAY_NAMES = new Set([
  'customer_super_admin',
  'vendor_super_admin',
  'customer_admin',
  'vendor_admin',
  'organization_administrator',
  'organization_admin',
  'org_admin',
  'org_administrator',
  'super_admin',
  'superadmin',
  'platform_admin',
  'admin',
]);

function normalize(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '')
    .replace(/[\s-]+/g, '_');
}

function matchesExportDisplayName(value?: string | null): boolean {
  const key = normalize(value);
  if (!key) return false;
  if (EXPORT_DISPLAY_NAMES.has(key)) return true;
  // "Customer Super Admin" / "Vendor Super Admin" already covered via underscores.
  // Also accept "Super Admin" variants that include a portal prefix we missed.
  return key.endsWith('_super_admin') || key === 'administrator';
}

/**
 * Returns true only for Super Admin / Admin (Org Admin).
 * Non-admin roles (manager, staff, viewer, RFQ manager, etc.) must get false so
 * Export stays hidden, while RFQ list/detail remain available to every logged-in user.
 */
export function canExportRfqData(
  role: string | null | undefined,
  portal: RfqExportPortal,
  roleName?: string | null,
  permissions?: readonly string[] | null,
): boolean {
  if (permissions?.includes(RFQ_EXPORT_PERMISSION)) return true;

  const roleKey = normalize(role);
  const allowedKeys =
    portal === 'customer'
      ? CUSTOMER_EXPORT_ROLE_KEYS
      : portal === 'vendor'
        ? VENDOR_EXPORT_ROLE_KEYS
        : ADMIN_EXPORT_ROLE_KEYS;

  if (roleKey && allowedKeys.has(roleKey)) return true;
  if (matchesExportDisplayName(role)) return true;
  if (matchesExportDisplayName(roleName)) return true;
  return false;
}
